import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useUserStore } from "store/userStore";
import { useStore } from "store/zustand";
import { getAuth, signOut } from "firebase/auth";
import { fetchUserCreditBalance } from "apis/creditService";
import CreditPurchaseModal from "pages/Credits/CreditPurchaseModal";
import TokensIcon from "assets/images/tokens.png";
import StandbyLogo from "assets/images/standby.png";
import LdActive from "assets/images/ld-active.png";
import Ld from "assets/images/ld.png";
import YrqActive from "assets/images/yrq-active.png";
import Yrq from "assets/images/yrq.png";
import ReqIcon from "assets/images/req.png";
import OpenActive from "assets/images/open-active.png";
import OpenIcon from "assets/images/open.png";
import AnsweredActive from "assets/images/answered-active.png";
import AnsweredIcon from "assets/images/answered.png";
import TokenIcon from "assets/images/token.png";
import TokenActiveIcon from "assets/images/token-active.png";
import LiveTrackingIcon from "assets/images/LiveTracking.png";
import LiveTrackingActiveIcon from "assets/images/LiveTracking-1.png";

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = useUserStore((state) => state.role);
  const { setUserRole } = useUserStore();
  const { logout } = useStore();

  const [isRequestsOpen, setIsRequestsOpen] = useState(false);
  const [creditBalance, setCreditBalance] = useState<string>("0");
  const [creditLoading, setCreditLoading] = useState(true);
  const [showCreditPurchaseModal, setShowCreditPurchaseModal] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Auto-expand Requests submenu if on a request-related page
  useEffect(() => {
    if (location.pathname === "/my-requests") {
      setIsRequestsOpen(true);
    }
  }, [location.pathname]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  // Fetch credit balance
  const fetchCredits = async () => {
    setCreditLoading(true);
    try {
      const balanceResponse = await fetchUserCreditBalance();
      setCreditBalance(String(balanceResponse.availableCredits));
    } catch (err) {
      console.error("Failed to fetch credit balance:", err);
    } finally {
      setCreditLoading(false);
    }
  };

  useEffect(() => {
    if (userRole === "user") {
      fetchCredits();
    }
  }, [userRole]);

  const logoutFunction = async () => {
    const auth = getAuth();
    try {
      await signOut(auth);
      logout();
      setUserRole(null);
      localStorage.removeItem("userDetails");
      localStorage.removeItem("token");
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  const navLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
      isActive
        ? "text-[#1F2937] font-semibold"
        : "text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#1F2937] font-medium"
    }`;

  const subNavLinkClass = ({ isActive }: { isActive: boolean }) =>
    `flex items-center gap-3 pl-11 pr-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
      isActive
        ? "bg-[#FEF3C7] text-[#D97706] font-medium"
        : "text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#6B7280]"
    }`;

  const adminRoutes = [
    { to: "/admin/dashboard", icon: "st-dashboard", label: "Dashboard" },
    { to: "/admin/user-management", icon: "st-user", label: "User Management" },
    { to: "/admin/credit-management", icon: "st-credits", label: "Token Management" },
    { to: "/admin/report&feedback", icon: "st-comment", label: "Report & Feedback" },
    { to: "/admin/discount-management", icon: "st-discount", label: "Discount Management" },
    { to: "/admin/non-rev-config", icon: "st-settings", label: "Non-Rev Config" },
    { to: "/admin/settings", icon: "st-settings", label: "Settings" },
  ];

  return (
    <>
      {/* Mobile toggle button */}
      <button
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed top-4 left-4 z-50 md:hidden bg-white p-2 rounded-lg shadow-lg"
        type="button"
      >
        <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          {isMobileOpen ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>

      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/30 z-40 md:hidden"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 h-full w-[250px] bg-white border-r border-[#E5E7EB] flex flex-col z-40 transition-transform duration-300 ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        }`}
      >
        {/* Logo */}
        <div className="px-6 py-5 border-b border-[#F3F4F6]">
          <NavLink to="/search" className="flex items-center gap-2">
            <img src={StandbyLogo} alt="Standby" className="h-8 w-auto object-contain" />
            <span className="text-[#1F2937] font-bold text-xl tracking-tight">STANDBY</span>
          </NavLink>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto px-4 py-4">
          {userRole === "admin" ? (
            <>
              <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider px-4 mb-3">
                Dashboard
              </p>
              <div className="space-y-1">
                {adminRoutes.map(({ to, icon, label }) => (
                  <NavLink key={to} to={to} className={navLinkClass}>
                    <i className={`${icon} text-lg`}></i>
                    <span>{label}</span>
                  </NavLink>
                ))}
              </div>
            </>
          ) : (
            <>
              <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider px-4 mb-3">
                Dashboard
              </p>
              <div className="space-y-1">
                {/* Loads */}
                <NavLink
                  to="/search"
                  className={navLinkClass}
                  end={false}
                >
                  {({ isActive }) => (
                    <>
                      <img src={isActive ? LdActive : Ld} alt="Loads" className="w-5 h-5" />
                      <span>Loads</span>
                    </>
                  )}
                </NavLink>

                {/* Your Requests */}
                <NavLink to="/my-loads" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <img src={isActive ? YrqActive : Yrq} alt="Your Requests" className="w-5 h-5" />
                      <span>Your Requests</span>
                    </>
                  )}
                </NavLink>

                {/* Requests (collapsible) */}
                <div>
                  <button
                    onClick={() => setIsRequestsOpen(!isRequestsOpen)}
                    className={`w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                      location.pathname === "/my-requests"
                        ? "text-[#1F2937] font-semibold"
                        : "text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#1F2937] font-medium"
                    }`}
                    type="button"
                  >
                    <div className="flex items-center gap-3">
                      <img src={ReqIcon} alt="Requests" className="w-5 h-5" />
                      <span>Requests</span>
                    </div>
                    <svg
                      className={`w-4 h-4 transition-transform duration-200 ${isRequestsOpen ? "rotate-180" : ""} ${
                        location.pathname === "/my-requests" ? "text-white" : "text-[#9CA3AF]"
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isRequestsOpen && (
                    <div className="mt-1 space-y-0.5">
                      <NavLink
                        to="/my-requests"
                        state={{ tab: "open" }}
                        className={({ isActive }) =>
                          `flex items-center gap-3 pl-8 pr-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                            isActive && location.state?.tab !== "answered"
                              ? "text-[#1F2937] font-semibold"
                              : "text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#6B7280]"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <img src={isActive && location.state?.tab !== "answered" ? OpenActive : OpenIcon} alt="Open" className="w-4 h-4" />
                            <span>Open</span>
                          </>
                        )}
                      </NavLink>
                      <NavLink
                        to="/my-requests"
                        state={{ tab: "answered" }}
                        className={({ isActive }) =>
                          `flex items-center gap-3 pl-8 pr-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${
                            isActive && location.state?.tab === "answered"
                              ? "text-[#1F2937] font-semibold"
                              : "text-[#9CA3AF] hover:bg-[#F3F4F6] hover:text-[#6B7280]"
                          }`
                        }
                      >
                        {({ isActive }) => (
                          <>
                            <img src={isActive && location.state?.tab === "answered" ? AnsweredActive : AnsweredIcon} alt="Answered" className="w-4 h-4" />
                            <span>Answered</span>
                          </>
                        )}
                      </NavLink>
                    </div>
                  )}
                </div>

                {/* Live Tracking */}
                <NavLink to="/live-tracking" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <img
                        src={isActive ? LiveTrackingActiveIcon : LiveTrackingIcon}
                        alt="Live Tracking"
                        className="w-5 h-5"
                      />
                      <span>Live Tracking</span>
                    </>
                  )}
                </NavLink>

                {/* Tokens */}
                <NavLink to="/credits" className={navLinkClass}>
                  {({ isActive }) => (
                    <>
                      <img src={isActive ? TokenActiveIcon : TokenIcon} alt="Tokens" className="w-5 h-5" />
                      <span>Tokens</span>
                    </>
                  )}
                </NavLink>
              </div>
            </>
          )}
        </nav>

        {/* Bottom Section */}
        <div className="border-t border-[#F3F4F6] px-8 py-4 space-y-2">
          {/* Token Balance Card - only for user role, hidden on credits page */}
          {userRole === "user" && location.pathname !== "/credits" && (
            <div className="bg-gradient-to-br from-[#1F2937] to-[#111827] rounded-2xl p-4 mb-3">
              <div className="flex items-center gap-3 mb-2">
                <img src={TokensIcon} alt="Tokens" className="w-10 h-10 object-contain" />
                <div>
                  <p className="text-white text-2xl font-bold leading-none">
                    {creditLoading ? "..." : creditBalance}
                  </p>
                  <p className="text-white/60 text-[11px] mt-0.5">Total Tokens Earned</p>
                </div>
              </div>
              <button
                onClick={() => setShowCreditPurchaseModal(true)}
                className="w-full bg-[#FBBF24] text-white text-sm font-semibold py-2.5 rounded-xl hover:bg-[#F59E0B] transition-colors"
              >
                Purchase Tokens
              </button>
            </div>
          )}

          {/* Settings */}
          {userRole === "user" && (
            <NavLink
              to="/settings"
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-xl text-sm transition-all duration-200 ${
                  isActive
                    ? "bg-[#F3F4F6] text-[#1F2937] font-medium"
                    : "text-[#6B7280] hover:bg-[#F3F4F6] hover:text-[#1F2937]"
                }`
              }
            >
              <svg className="w-5 h-5 text-[#9CA3AF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span>Settings</span>
            </NavLink>
          )}

          {/* Logout */}
          <button
            onClick={logoutFunction}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-[#6B7280] hover:bg-red-50 hover:text-red-600 transition-all duration-200"
            type="button"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            <span>Log out</span>
          </button>
        </div>
      </aside>

      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        onPaymentComplete={fetchCredits}
        isOpen={showCreditPurchaseModal}
        onClose={() => setShowCreditPurchaseModal(false)}
      />
    </>
  );
};

export default Sidebar;
